/**
 * Enhanced Logger for Chrome Extension (Vite + React + MV3)
 *
 * Features:
 * - Auto-context detection (background/popup/content/options/devtools)
 * - Level-based filtering with environment variable support
 * - Colorized, timestamped output in development
 * - Content script → Background forwarding (dev-only)
 * - Runtime level switching (dev-only)
 * - Production-safe with configurable console.error preservation
 */

// Log levels with numeric values for comparison
const LOG_LEVELS = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

// Color scheme for development logging
const LEVEL_COLORS = {
    debug: '#888', // gray
    info: '#0366d6', // blue
    warn: '#b08900', // yellow
    error: '#d73a49', // red
};

// Current runtime level (can be changed in dev)
let currentLevel = null;
let isDev = false;
let context = 'unknown';

// --- Build-time env capture (Vite) with Node/test fallback ---
// Vite replaces direct references like import.meta.env.VITE_*** at build time.
// Access them once here so the values are inlined in built code (Chrome extension runtime won't have import.meta).
function __readViteEnv(key, fallback) {
    try {
        // Use static property access so Vite can inline at build time
        switch (key) {
            case 'VITE_LOG_LEVEL':
                return import.meta.env.VITE_LOG_LEVEL; // eslint-disable-line no-undef
            case 'VITE_PRESERVE_CONSOLE_ERROR':
                return import.meta.env.VITE_PRESERVE_CONSOLE_ERROR; // eslint-disable-line no-undef
            case 'VITE_ENABLE_FORWARDING':
                return import.meta.env.VITE_ENABLE_FORWARDING; // eslint-disable-line no-undef
            case 'MODE':
                return import.meta.env.MODE; // eslint-disable-line no-undef
            case 'DEV':
                return import.meta.env.DEV; // eslint-disable-line no-undef
            case 'PROD':
                return import.meta.env.PROD; // eslint-disable-line no-undef
            case 'NODE_ENV':
                // Not exposed by Vite; fall back to process.env below
                return fallback;
            default:
                return fallback;
        }
    } catch (_e) {
        return fallback;
    }
}

const ENV = {
    // Explicitly list all env keys we rely on so Vite includes them
    VITE_LOG_LEVEL: __readViteEnv(
        'VITE_LOG_LEVEL',
        typeof process !== 'undefined' ? process.env?.VITE_LOG_LEVEL : undefined
    ), // eslint-disable-line no-undef
    VITE_PRESERVE_CONSOLE_ERROR: __readViteEnv(
        'VITE_PRESERVE_CONSOLE_ERROR',
        typeof process !== 'undefined' ? process.env?.VITE_PRESERVE_CONSOLE_ERROR : undefined
    ), // eslint-disable-line no-undef
    VITE_ENABLE_FORWARDING: __readViteEnv(
        'VITE_ENABLE_FORWARDING',
        typeof process !== 'undefined' ? process.env?.VITE_ENABLE_FORWARDING : undefined
    ), // eslint-disable-line no-undef
    MODE: __readViteEnv(
        'MODE',
        typeof process !== 'undefined' ? process.env?.MODE || process.env?.NODE_ENV : undefined
    ), // eslint-disable-line no-undef
    NODE_ENV: __readViteEnv(
        'NODE_ENV',
        typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined
    ), // eslint-disable-line no-undef
    DEV: __readViteEnv('DEV', undefined),
    PROD: __readViteEnv('PROD', undefined),
};

/**
 * Safe context detection using multiple Chrome extension APIs
 */
function detectContext() {
    try {
        // Check if we're in a service worker (MV3 background script)
        if (
            typeof ServiceWorkerGlobalScope !== 'undefined' &&
            typeof self !== 'undefined' &&
            self instanceof ServiceWorkerGlobalScope
        ) {
            // eslint-disable-line no-undef
            return 'background';
        }

        // Check if we have window object (not in service worker)
        if (typeof window === 'undefined') {
            return 'background'; // Likely background script without window
        }

        // Check for MV2 background page (fallback)
        if (
            typeof chrome !== 'undefined' &&
            chrome.extension?.getBackgroundPage &&
            window === chrome.extension.getBackgroundPage()
        ) {
            return 'background';
        }

        // Check if we're in a content script context
        if (window.location && typeof chrome !== 'undefined' && chrome.runtime?.id) {
            const extensionOrigin = `chrome-extension://${chrome.runtime.id}`;
            if (window.location.origin !== extensionOrigin) {
                return 'content';
            }

            // We're in an extension page, determine which one
            const path = window.location.pathname;
            if (path.includes('popup') || path.includes('index.html')) {
                return 'popup';
            } else if (path.includes('options') || path.includes('settings')) {
                return 'options';
            } else if (path.includes('devtools')) {
                return 'devtools';
            } else {
                return 'popup'; // Default for extension pages
            }
        }

        return 'app'; // Fallback for regular web app context
    } catch (error) {
        return 'unknown';
    }
}

/**
 * Get timestamp string in HH:MM:SS.mmm format
 */
function getTimestamp() {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const milliseconds = now.getMilliseconds().toString().padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * Safely serialize value for message passing
 */
function serializeForMessage(value) {
    if (value === null || value === undefined) {
        return value;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (value instanceof Error) {
        return {
            __error: true,
            name: value.name,
            message: value.message,
            stack: value.stack,
        };
    }

    try {
        // Try JSON serialization for objects
        const serialized = JSON.parse(JSON.stringify(value));
        return serialized;
    } catch (error) {
        // Fallback to string representation
        return String(value);
    }
}

/**
 * Get environment variable safely
 */
function getEnvVar(key) {
    try {
        // Read from pre-captured ENV (inlined by Vite) with Node fallback handled above
        return ENV[key];
    } catch (error) {
        return undefined;
    }
}

/**
 * Check if we're in development mode
 */
function isDevelopmentMode() {
    try {
        // In build outputs, import.meta.env.DEV is false even when --mode development.
        // So prioritize MODE === 'development' first.
        if (ENV.MODE === 'development') return true;
        // Prefer Vite's DEV flag when available (dev server).
        if (typeof ENV.DEV !== 'undefined') return Boolean(ENV.DEV);
        const mode = ENV.MODE || ENV.NODE_ENV;
        if (typeof mode === 'string') return mode === 'development';

        // Fallback: assume development if localhost
        if (typeof location !== 'undefined') {
            return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
        }

        return false;
    } catch (error) {
        return false;
    }
}

/**
 * Initialize logger configuration
 */
function initializeLogger() {
    // Detect development mode
    isDev = isDevelopmentMode();

    // Detect context
    context = detectContext();

    // Set initial log level from environment or default
    const envLevel = getEnvVar('VITE_LOG_LEVEL');
    const defaultLevel = isDev ? 'debug' : 'error';
    currentLevel = envLevel || defaultLevel;

    // In development, try to load saved runtime level from storage
    if (isDev && typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
        chrome.storage.local
            .get(['logLevel'])
            .then((result) => {
                if (
                    result.logLevel &&
                    Object.prototype.hasOwnProperty.call(LOG_LEVELS, result.logLevel)
                ) {
                    currentLevel = result.logLevel;
                }
            })
            .catch(() => {
                // Ignore storage errors
            });
    }
}

/**
 * Check if a log level should be output
 */
function shouldLog(level) {
    return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel];
}

/**
 * Format log message for development output
 */
function formatDevMessage(level, contextName, ...args) {
    const timestamp = getTimestamp();
    const levelBadge = level.toUpperCase();
    const color = LEVEL_COLORS[level];

    return {
        prefix: `%c${timestamp} %c[${contextName}] %c${levelBadge}`,
        styles: [
            'color: #666;',
            'color: #333; font-weight: bold;',
            `color: ${color}; font-weight: bold;`,
        ],
        args,
    };
}

/**
 * Forward content script log to background (dev-only)
 */
function forwardToBackground(level, contextName, ...args) {
    if (!isDev || context !== 'content') {
        return;
    }

    // Check if forwarding is enabled
    const enableForwarding = getEnvVar('VITE_ENABLE_FORWARDING') !== 'false';

    if (!enableForwarding) {
        return;
    }

    try {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
            chrome.runtime
                .sendMessage({
                    __logger: true,
                    level,
                    context: contextName,
                    timestamp: getTimestamp(),
                    args: args.map(serializeForMessage),
                })
                .catch(() => {
                    // Ignore forwarding errors to avoid noise
                });
        }
    } catch (error) {
        // Ignore forwarding errors
    }
}

/**
 * Core logging function
 */
function log(level, contextName, ...args) {
    if (!shouldLog(level)) {
        return;
    }

    // Forward to background if in content script (dev-only)
    forwardToBackground(level, contextName, ...args);

    if (isDev) {
        // Development: colorized, timestamped output
        const formatted = formatDevMessage(level, contextName, ...args);
        console[level](formatted.prefix, ...formatted.styles, ...formatted.args); // eslint-disable-line no-console
    } else {
        // Production: minimal output
        if (level === 'error') {
            // Always preserve console.error in production (unless configured otherwise)
            const preserveError = getEnvVar('VITE_PRESERVE_CONSOLE_ERROR') !== 'false';

            if (preserveError) {
                console.error(`[${contextName}]`, ...args);
            }
        }
        // Other levels are stripped in production
    }
}

/**
 * Logger class
 */
class Logger {
    constructor(contextName = null) {
        this.contextName = contextName || context;
    }

    debug(...args) {
        log('debug', this.contextName, ...args);
    }

    info(...args) {
        log('info', this.contextName, ...args);
    }

    warn(...args) {
        log('warn', this.contextName, ...args);
    }

    error(...args) {
        log('error', this.contextName, ...args);
    }

    /**
     * Set log level at runtime (development only)
     */
    setLevel(level) {
        if (!isDev) {
            this.warn('Runtime level switching is only available in development mode');
            return;
        }

        if (!Object.prototype.hasOwnProperty.call(LOG_LEVELS, level)) {
            this.error('Invalid log level:', level, 'Valid levels:', Object.keys(LOG_LEVELS));
            return;
        }

        currentLevel = level;

        // Persist to chrome storage
        if (typeof chrome !== 'undefined' && chrome.storage?.local) {
            chrome.storage.local.set({ logLevel: level }).catch(() => {
                // Ignore storage errors
            });
        }

        this.info('Log level changed to:', level);
    }

    /**
     * Get current log level
     */
    getLevel() {
        return currentLevel;
    }

    /**
     * Get logger context
     */
    getContext() {
        return this.contextName;
    }
}

/**
 * Create a logger with optional context name
 */
export function createLogger(contextName = null) {
    return new Logger(contextName);
}

// Initialize on module load
initializeLogger();

// Create and export default logger
const logger = new Logger();

// Setup background listener for forwarded messages (dev-only)
if (
    isDev &&
    context === 'background' &&
    typeof chrome !== 'undefined' &&
    chrome.runtime &&
    chrome.runtime.onMessage
) {
    chrome.runtime.onMessage.addListener((message) => {
        if (!message || !message.__logger) {
            return;
        }

        const { level, context: msgContext, timestamp, args } = message;

        // Re-log forwarded message with clear indication
        const prefix = `%c${timestamp} %c[${msgContext}] (forwarded) %c${level.toUpperCase()}`;
        const styles = [
            'color: #666;',
            'color: #333; font-style: italic;',
            `color: ${LEVEL_COLORS[level]}; font-weight: bold;`,
        ];

        console[level](prefix, ...styles, ...args); // eslint-disable-line no-console
    });
}

export default logger;

// Expose minimal diagnostics in extension background for troubleshooting
try {
    if (typeof globalThis !== 'undefined') {
        globalThis.__LOGGER_DIAG__ = {
            get env() {
                return ENV;
            },
            get level() {
                return currentLevel;
            },
            get context() {
                return context;
            },
        };
    }
} catch (_) {
    /* ignore */
}
